// Wizard's password-reset landing page. Mirrors the dashboard's
// frontend/src/pages/ResetPassword.tsx logic — same recovery-session handling,
// same supabase.auth.updateUser({ password }) call — with wizard styling.
//
// Reached via email link when a user clicked "Forgot password?" on the
// wizard's staff sign-in form. Supabase appends `#access_token=...` to the
// URL; the supabase-js client (constructed with `detectSessionInUrl: true`)
// auto-parses it and creates a recovery session. We then surface a
// new-password form that calls updateUser.

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, Lock, Loader2 } from 'lucide-react';
import { selfServiceSupabase } from '../../lib/selfServiceSupabase';
import PrimaryButton from '../../components/self-service/shared/PrimaryButton';
import Banner from '../../components/self-service/shared/Banner';

const ResetPasswordPage: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Capture into a non-null local so TS narrowing survives across the
    // async closures below (module-level exports don't stay narrowed
    // through `await` / setTimeout boundaries).
    const supabase = selfServiceSupabase;
    if (!supabase) {
      setError('Supabase Auth is not configured.');
      return;
    }
    // Supabase processes the URL hash fragment asynchronously when
    // detectSessionInUrl is true. Wait briefly, then read the session.
    // Retry once if the first read came back empty but the hash is still
    // present — covers a brief race on slow browsers.
    const checkSession = async () => {
      await new Promise((resolve) => setTimeout(resolve, 100));
      const { data, error: sessErr } = await supabase.auth.getSession();
      if (sessErr) {
        setError('Error validating reset link. Please request a new password reset.');
        return;
      }
      if (!data.session) {
        const hash = window.location.hash;
        if (hash && hash.includes('access_token')) {
          setTimeout(async () => {
            const { data: retry } = await supabase.auth.getSession();
            if (!retry.session) {
              setError('Invalid or expired reset link. Request a new password reset.');
            }
          }, 500);
        } else {
          setError('Invalid or expired reset link. Request a new password reset.');
        }
      }
    };
    void checkSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!selfServiceSupabase) {
      setError('Supabase Auth is not configured.');
      return;
    }
    if (!newPassword || !confirmPassword) {
      setError('Fill in both password fields.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      // Write `password_set: true` alongside the password so the backend
      // has-password signal reflects the reset. See hasUserSetPassword in
      // email-dispatch.controller for why we key off metadata rather than
      // encrypted_password.
      const { error: updateError } = await selfServiceSupabase.auth.updateUser({
        password: newPassword,
        data: { password_set: true },
      });
      if (updateError) throw updateError;
      setSuccess(true);
      // Sign out so the user must re-authenticate with the new password,
      // matching the dashboard's flow.
      await selfServiceSupabase.auth.signOut();
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 max-w-md w-full">
        <h1 className="text-xl font-semibold text-slate-900 mb-2">Reset password</h1>
        <p className="text-sm text-slate-600 mb-6">
          Choose a new password. It works for both the wizard and the CTV Buyer dashboard.
        </p>

        {success ? (
          <Banner tone="success" label="Password updated" icon={<CheckCircle className="w-3 h-3" />}>
            Redirecting…
          </Banner>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
                New password
              </span>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
                Confirm password
              </span>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-type your new password"
                  required
                  minLength={8}
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </label>

            {error && (
              <Banner tone="warn" label="Couldn't reset" icon={<AlertCircle className="w-3 h-3" />}>
                {error}
              </Banner>
            )}

            <div className="flex items-center justify-between gap-2 pt-2">
              <Link
                to="/"
                onClick={() => selfServiceSupabase?.auth.signOut()}
                className="text-sm text-slate-600 hover:text-slate-900 hover:underline"
              >
                Return to home
              </Link>
              <PrimaryButton type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {isLoading ? 'Resetting…' : 'Reset password'}
              </PrimaryButton>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
